<!-- 帮助文档页：Vue2 + Element UI（el-tree / el-pagination），本页不使用 jQuery -->
<template>
  <div class="help-doc-page">
    <div id="top">
      <div id="nav">
        <h1>
          <img :src="`${publicPath}static/images/logo.png`" />
          <a href="http://www.jsdnn.com" target="_blank">江苏狄诺尼</a>
          <i />
          <a href="index.html">
            <span id="softName">{{ softName }}</span
            >-帮助
          </a>
        </h1>
      </div>
      <div class="search-box" :class="{ 'input-default': searchDefault }">
        <span ref="ruler" id="ruler" />
        <input
          id="search"
          type="text"
          autocomplete="off"
          v-model="searchKeyword"
          :placeholder="searchPlaceholder"
          @focus="onSearchFocus"
          @blur="onSearchBlur"
          @input="updateClearIconPosition"
          @keydown.enter="onSearchEnter"
        />
        <i class="iconfont icon-sousuo search-icon" />
        <i
          v-if="showSearchClear"
          class="iconfont icon-guanbi search-clear"
          :style="{ marginLeft: clearIconMargin }"
          @mousedown.prevent
          @click="clearSearch"
        />
      </div>
    </div>
    <!-- 左侧菜单 -->
    <div id="menu">
      <h2>
        <span>目录</span>
        <i class="iconfont icon-caidan menuIcon" @click="expandAll" />
      </h2>
      <!-- el-tree 目录树，保留 id 以兼容原有 CSS 布局 -->
      <el-tree
        ref="menuTree"
        id="menuTree"
        class="help-menu-tree showIcon"
        :data="treeData"
        node-key="treeKey"
        :props="treeProps"
        :indent="16"
        highlight-current
        :expand-on-click-node="true"
        @node-click="onTreeClick"
      />
    </div>
    <!-- 右侧内容 -->
    <div id="main" :class="mainClass">
      <!-- 菜单对应的内容页面 -->
      <div id="content">
        <h2 v-if="docTitle">{{ docTitle }}</h2>
        <div v-html="docContent" />
      </div>

      <!-- 搜索结果 -->
      <div id="search_page" v-show="showSearchPage">
        <span
          class="iconfont search_page_icon"
          :class="searchPageCollapsed ? 'icon-iconfront-' : 'icon-iconfront-1'"
          @click="toggleSearchPage"
        />
        <div id="appendInfo" class="appendInfo">
          <label v-if="searchSummary" class="info">{{ searchSummary }}</label>
          <ul v-if="searchList.length > 0">
            <li
              v-for="item in searchList"
              :key="item.menuId"
              :data-id="item.menuId"
              @click="openSearchResult(item.menuId)"
            >
              <h4>{{ item.label }}</h4>
              <p v-html="item.line"></p>
            </li>
          </ul>
          <p v-else-if="searchEmptyText" v-html="searchEmptyText" />
        </div>

        <!-- 分页：Element UI，总页数 <= 1 时隐藏（等同原 isHide） -->
        <div id="paging" v-show="showPaging && searchTotal > pageSize">
          <el-pagination
            :current-page.sync="pageNumber"
            :page-size="pageSize"
            :total="searchTotal"
            layout="prev, pager, next"
            @current-change="onPageChange"
          />
        </div>
      </div>
      <!-- 底部信息 -->
      <div id="footer">
        <p>
          江苏狄诺尼信息技术有限责任公司
          <!-- <a href="http://www.beian.miit.gov.cn/" target="_blank">苏ICP备18062906号</a> -->
          <span style="color: #1c70de">苏ICP备18062906号</span>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getSoftwareByAlias,
  getSoftwareDetail,
  helpSearch,
} from "@/api/helpDocuments";

/** 判断是否为已嵌套的树形数据 */
function isNestedTree(list) {
  return list.some((item) => {
    const childList = item.children || item.nodes;
    return Array.isArray(childList) && childList.length > 0;
  });
}

/** 嵌套树克隆，保留 children 结构；生成唯一 treeKey 供 el-tree 使用 */
function cloneNestedTree(nodes, pathPrefix = "") {
  return nodes.map((item, index) => {
    const childList = item.children || item.nodes;
    const id = item.id != null ? item.id : item.menuId;
    const path = `${pathPrefix}/${index}`;
    const treeKey = item.helpid || `${path}_${id}`;
    const node = { ...item, id, treeKey };
    if (Array.isArray(childList) && childList.length > 0) {
      node.children = cloneNestedTree(childList, path);
    } else {
      delete node.children;
      delete node.nodes;
    }
    return node;
  });
}

/** 读取父节点 id（兼容 pId / pid / parentId） */
function getParentId(item) {
  const parentId = item.pId != null ? item.pId : item.pid;
  if (parentId != null) {
    return parentId;
  }
  return item.parentId;
}

/** 是否为根节点 */
function isRootNode(parentId, nodeMap, selfId) {
  if (
    parentId == null ||
    parentId === "" ||
    parentId === 0 ||
    parentId === "0" ||
    parentId === -1 ||
    parentId === "-1"
  ) {
    return true;
  }
  const parentKey = String(parentId);
  if (!nodeMap[parentKey] || parentKey === String(selfId)) {
    return true;
  }
  return false;
}

/** 将 zTree 扁平数据（id/pId）转为 el-tree 嵌套结构；若已是嵌套则直接使用 */
function buildTreeData(flatList) {
  if (!Array.isArray(flatList) || flatList.length === 0) {
    return [];
  }

  if (isNestedTree(flatList)) {
    return cloneNestedTree(flatList);
  }

  return buildTreeFromFlat(flatList);
}

/** 由扁平 id/pId 列表组装 el-tree 数据 */
function buildTreeFromFlat(flatList) {
  const nodeMap = {};
  const roots = [];

  flatList.forEach((item) => {
    const id = item.id != null ? item.id : item.menuId;
    nodeMap[String(id)] = { ...item, id, children: [] };
  });

  flatList.forEach((item) => {
    const id = item.id != null ? item.id : item.menuId;
    const node = nodeMap[String(id)];
    const parentId = getParentId(item);

    if (isRootNode(parentId, nodeMap, id)) {
      roots.push(node);
    } else {
      nodeMap[String(parentId)].children.push(node);
    }
  });

  const trimEmptyChildren = (nodes) => {
    nodes.forEach((node) => {
      if (node.children && node.children.length > 0) {
        trimEmptyChildren(node.children);
      } else {
        delete node.children;
      }
    });
  };
  trimEmptyChildren(roots);

  const assignTreeKeys = (nodes, pathPrefix = "") => {
    nodes.forEach((node, index) => {
      const path = `${pathPrefix}/${index}`;
      node.treeKey = node.helpid || `${path}_${node.id}`;
      if (node.children && node.children.length > 0) {
        assignTreeKeys(node.children, path);
      }
    });
  };
  assignTreeKeys(roots);

  return roots;
}

/** 在树中按条件查找节点 */
function findTreeNodes(nodes, predicate, result = []) {
  nodes.forEach((node) => {
    if (predicate(node)) {
      result.push(node);
    }
    if (node.children && node.children.length > 0) {
      findTreeNodes(node.children, predicate, result);
    }
  });
  return result;
}

/** 按 id 查找单个节点 */
function findNodeById(nodes, id) {
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    if (String(node.id) === String(id)) {
      return node;
    }
    if (node.children && node.children.length > 0) {
      const found = findNodeById(node.children, id);
      if (found) {
        return found;
      }
    }
  }
  return null;
}

export default {
  name: "HelpDocuments",
  data() {
    return {
      publicPath: process.env.BASE_URL,

      // 软件与 URL 参数
      softName: "",
      softId: null,
      soft: null,
      alias: null,
      version: "",
      language: "zh",
      cmd: null,
      module: null,

      // 目录树
      treeData: [],
      treeProps: {
        label: "label",
        children: "children",
      },
      expandFlag: true,

      // 文档内容
      docTitle: "",
      docContent: "",

      // 搜索框
      searchPlaceholder: "搜索问题关键字，找到答案",
      searchKeyword: "",
      searchDefault: true,
      clearIconMargin: "0px",

      // 搜索结果
      showSearchPage: false,
      searchPageCollapsed: false,
      searchSummary: "",
      searchList: [],
      searchEmptyText: "",
      searchTotal: 0,
      keyword: "",
      pageNumber: 1,
      pageSize: 5,
      showPaging: false,
    };
  },
  computed: {
    /** 主内容区 class，控制搜索模式与面板折叠 */
    mainClass() {
      return {
        searchBox: this.showSearchPage,
        search_page_con_box: this.searchPageCollapsed,
      };
    },
    /** 输入框有内容时显示清除按钮（含空格） */
    showSearchClear() {
      return this.searchKeyword !== "";
    },
  },
  watch: {
    /** soft/alias/version/language 变化时重新加载菜单 */
    "$route.query": {
      handler(query, oldQuery) {
        if (!oldQuery) {
          return;
        }
        const keys = ["soft", "alias", "version", "language"];
        const changed = keys.some(
          (key) => String(query[key] || "") !== String(oldQuery[key] || ""),
        );
        if (!changed) {
          return;
        }
        this.syncQueryFromRoute();
        this.loadSoftwareMenu();
      },
    },
  },
  mounted() {
    this.syncQueryFromRoute();
    this.loadSoftwareMenu();
  },
  methods: {
    /** Loading 提示 */
    getLoading() {
      return this.$message({
        message: "加载中...",
        type: "success",
        iconClass: "el-icon-loading",
        duration: 0,
      });
    },

    // ---------- URL 参数 ----------

    /** 从路由 query 同步 soft/alias/version/language/cmd/module */
    syncQueryFromRoute() {
      const query = this.$route.query;
      this.soft = query.soft || query.alias || null;
      this.alias = query.alias || query.soft || null;
      this.version = query.version || "";
      this.language = query.language || "zh";
      this.cmd = query.cmd || null;
      this.module = query.module || null;
    },

    /** 更新 URL 中的 soft/module/cmd 参数（保留 version/language） */
    updateUrl() {
      const query = { ...this.$route.query };
      if (this.soft) {
        query.soft = this.soft;
      }
      if (this.module != null) {
        query.module = this.module;
      }
      if (this.cmd) {
        query.cmd = this.cmd;
      }
      this.$router.replace({
        path: this.$route.path,
        query,
      });
    },

    // ---------- 菜单与 el-tree ----------

    /** 加载软件菜单并渲染 el-tree */
    async loadSoftwareMenu() {
      const alias = this.alias || this.soft;
      if (!alias) {
        return;
      }

      const loading = this.getLoading();
      try {
        const res = await getSoftwareByAlias(alias, {
          alias,
          language: this.language,
          version: this.version,
        });
        if (res.code !== 200) {
          return;
        }

        const softData = res.data;
        this.softId = softData.id;
        if (this.soft == null) {
          this.soft = softData.alias;
          this.alias = softData.alias;
          this.updateUrl();
        }
        this.softName = softData.name;

        const menuNodes = JSON.parse(softData.menu);
        this.treeData = buildTreeData(menuNodes);

        this.$nextTick(() => {
          this.openDefaultNode();
        });
      } catch (e) {
        console.error("loadSoftwareMenu error=>", e);
      } finally {
        loading.close();
      }
    },

    /** 树节点点击：同步 URL、加载文档 */
    onTreeClick(data, node) {
      if (data.cmd && node.level > 0) {
        this.cmd = data.cmd;
      } else {
        this.cmd = "-1";
      }

      this.module = this.getNodeRootCMD(node);
      this.updateUrl();
      this.loadDocument(data);
    },

    /** 取得节点父级根节点的 cmd */
    getNodeRootCMD(node) {
      let current = node;
      while (current.parent && current.parent.data) {
        current = current.parent;
      }
      let mCmd = current.data.cmd;
      if (mCmd === "" || mCmd == null) {
        mCmd = -1;
      }
      return mCmd;
    },

    /** 根据 URL 中的 module/cmd 打开默认节点 */
    openDefaultNode() {
      const tree = this.$refs.menuTree;
      if (!tree || !this.treeData.length) {
        return;
      }

      let parentNode = null;

      // 如果指定模块，搜索一级菜单（treeData 顶层）
      if (this.module != null) {
        parentNode =
          this.treeData.find(
            (item) => String(item.cmd) === String(this.module),
          ) || null;
      }

      if (this.cmd != null) {
        let results = [];
        if (parentNode) {
          results = findTreeNodes([parentNode], (item) => {
            return String(item.cmd) === String(this.cmd);
          });
        } else {
          results = findTreeNodes(this.treeData, (item) => {
            return String(item.cmd) === String(this.cmd);
          });
        }

        if (results.length !== 0) {
          const target = results[0];
          tree.setCurrentKey(target.treeKey);
          const targetNode = tree.getNode(target.treeKey);
          if (targetNode) {
            this.onTreeClick(target, targetNode);
          }
        } else if (parentNode) {
          tree.setCurrentKey(parentNode.treeKey);
          const parentTreeNode = tree.getNode(parentNode.treeKey);
          if (parentTreeNode) {
            parentTreeNode.expanded = true;
          }
        }
      } else if (parentNode) {
        tree.setCurrentKey(parentNode.treeKey);
        const parentTreeNode = tree.getNode(parentNode.treeKey);
        if (parentTreeNode) {
          parentTreeNode.expanded = true;
        }
      }
    },

    /** 目录全部展开/收起 */
    expandAll() {
      const tree = this.$refs.menuTree;
      if (!tree) {
        return;
      }

      const shouldExpand = this.expandFlag;
      const walk = (nodes) => {
        nodes.forEach((data) => {
          const node = tree.getNode(data.treeKey);
          if (node && node.childNodes && node.childNodes.length > 0) {
            node.expanded = shouldExpand;
          }
          if (data.children && data.children.length > 0) {
            walk(data.children);
          }
        });
      };
      walk(this.treeData);
      this.expandFlag = !this.expandFlag;
    },

    // ---------- 文档内容 ----------

    /** 加载节点对应文档详情 */
    async loadDocument(treeNode) {
      const helpid = treeNode.id;
      if (helpid === undefined || helpid == null || helpid === "") {
        return;
      }

      const loading = this.getLoading();
      try {
        const res = await getSoftwareDetail(helpid, this.softId);
        if (res.code !== 200) {
          return;
        }

        // data 可能为 {} 或 { content: "..." }，无 content 时正文保持空白
        this.docTitle = treeNode.label;
        this.docContent = res.data && res.data.content ? res.data.content : "";
      } catch (e) {
        console.error("loadDocument error=>", e);
      } finally {
        loading.close();
      }
    },

    // ---------- 搜索 ----------

    /** 计算输入文本宽度（用于清除按钮定位） */
    getTextWidth(text) {
      const ruler = this.$refs.ruler;
      if (!ruler) {
        return 0;
      }
      ruler.textContent = text;
      return ruler.offsetWidth;
    },

    /** 更新清除按钮位置 */
    updateClearIconPosition() {
      if (this.searchKeyword === "") {
        return;
      }
      this.searchDefault = false;
      const len = this.getTextWidth(this.searchKeyword);
      this.clearIconMargin = `${len / 2 + 16}px`;
    },

    onSearchFocus() {
      if (this.searchDefault) {
        this.searchKeyword = "";
        this.searchDefault = false;
      }
    },

    onSearchBlur() {
      if (this.searchKeyword === "") {
        this.searchDefault = true;
        this.resetSearchView();
      } else {
        this.updateClearIconPosition();
      }
    },

    /** Enter 触发搜索 */
    onSearchEnter(event) {
      const keyword = this.searchKeyword.trim();
      if (event.keyCode === 13 && keyword !== "") {
        this.searchDefault = false;
        this.updateClearIconPosition();

        this.showSearchPage = true;
        this.searchPageCollapsed = false;
        this.keyword = keyword;
        this.pageNumber = 1;
        this.searchTotal = 0;
        this.searchSummary = "";
        this.searchList = [];
        this.searchEmptyText = "";
        this.showPaging = false;

        this.getSearchList();
      }
    },

    /** 清除搜索框与搜索结果 */
    clearSearch() {
      this.searchKeyword = "";
      this.searchDefault = true;
      this.resetSearchView();
    },

    /** 重置搜索相关视图 */
    resetSearchView() {
      this.showSearchPage = false;
      this.searchPageCollapsed = false;
      this.searchSummary = "";
      this.searchList = [];
      this.searchEmptyText = "";
      this.showPaging = false;
    },

    /** 翻页 */
    onPageChange(page) {
      this.pageNumber = page;
      this.getSearchList();
    },

    /** 请求搜索列表 */
    async getSearchList() {
      const loading = this.getLoading();
      try {
        const res = await helpSearch({
          keyWord: this.keyword,
          softwareId: this.softId,
          pageNum: this.pageNumber,
          pageSize: this.pageSize,
        });

        if (res.code !== 200) {
          return;
        }

        this.searchTotal = res.data.total;
        const infoList = res.data.rows || [];

        if (this.searchTotal > 0) {
          const pageAll = Math.ceil(this.searchTotal / this.pageSize);
          this.searchSummary = `搜索结果：${this.searchTotal}，共${pageAll}页`;
          this.searchEmptyText = "";
          this.searchList = infoList.map((item) => {
            const node = findNodeById(this.treeData, item.menuId);
            return {
              menuId: item.menuId,
              label: node ? node.label : "",
              line: item.line,
            };
          });
          this.showPaging = true;
        } else {
          this.searchSummary = "";
          this.searchList = [];
          this.searchEmptyText = `未找到任何有关 <b>${this.keyword}</b> 的结果`;
          this.showPaging = false;
        }
      } catch (e) {
        console.error("getSearchList error=>", e);
      } finally {
        loading.close();
      }
    },

    /** 折叠/展开搜索结果面板 */
    toggleSearchPage() {
      this.searchPageCollapsed = !this.searchPageCollapsed;
    },

    /** 点击搜索结果，定位并加载对应文档 */
    openSearchResult(menuId) {
      const tree = this.$refs.menuTree;
      if (!tree) {
        return;
      }

      const data = findNodeById(this.treeData, menuId);
      if (!data) {
        return;
      }

      tree.setCurrentKey(data.treeKey);
      const node = tree.getNode(data.treeKey);
      if (node) {
        this.onTreeClick(data, node);
      }
    },
  },
};
</script>

<style lang="scss">
.help-doc-page {
  position: relative;
  min-height: 100vh;
  width: 100%;
  background: #fff;
  #top #nav h1 a {
    font-size: 16px;
  }
  #main #footer {
    font-size: 14px;
  }
  #top .search-box .iconfont.search-clear {
    display: block !important;
    margin-top: -6px !important;
  }

  /* el-tree 样式尽量贴近原 zTree 目录 */
  #menu .help-menu-tree {
    background: transparent;
    font-size: 10pt;
    font-family: "Microsoft Yahei", Verdana, Simsun, "Segoe UI",
      "Helvetica Neue", Arial, sans-serif;
    padding: 5px;
    color: #333 !important;
    .el-tree-node__content {
      height: auto;
      min-height: 30px;
      line-height: 1.5;
      align-items: flex-start;
      padding: 4px 0;
      white-space: normal;
    }

    .el-tree-node__label {
      flex: 1;
      min-width: 0;
      white-space: normal;
      word-break: break-all;
      overflow: visible;
      text-overflow: unset;
      line-height: 1.5;
    }

    .el-tree-node__content:hover {
      background-color: #e7e7e7;
    }

    .el-tree-node.is-current > .el-tree-node__content {
      background-color: #d4d4d4;
    }

    /* 展开/折叠图标（原 zTree showIcon） */
    .el-tree-node__expand-icon {
      visibility: visible;
      width: 16px;
      height: 16px;
      padding: 0;
      margin-top: 2px;
      margin-right: 4px;
      flex-shrink: 0;
      font-size: 14px;
      color: #666;
    }

    .el-tree-node__expand-icon.is-leaf {
      visibility: hidden;
    }

    /* 一级节点加粗 */
    & > .el-tree-node > .el-tree-node__content .el-tree-node__label {
      font-size: 120%;
      font-weight: bold;
    }
  }

  /* el-pagination：覆盖 index.css 中 #paging span/a 的 float 规则，避免图标错位 */
  #paging {
    .el-pagination {
      padding-left: 0;
      display: flex;
      align-items: center;
      flex-wrap: wrap;

      .btn-prev,
      .btn-next,
      .el-pager li {
        float: none;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 30px;
        width: 30px;
        height: 30px;
        line-height: 1;
        padding: 0;
        margin: 0 10px 0 0;
        background: #f0f2f5;
        border-radius: 2px;
        font-size: 13px;
        color: #666;
        box-sizing: border-box;
      }

      .btn-prev,
      .btn-next {
        border: none;
      }

      /* 重置全局 #paging span 对分页内部元素的干扰 */
      .btn-prev span,
      .btn-next span,
      .el-pager li span,
      .el-pager li a {
        float: none !important;
        width: auto !important;
        height: auto !important;
        min-width: 0 !important;
        line-height: 1 !important;
        margin: 0 !important;
        padding: 0 !important;
        background: transparent !important;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      .el-pager {
        display: inline-flex;
        align-items: center;
        margin: 0;
        padding: 0;
      }

      .el-pager li.active {
        background: #1c70de;
        color: #fff;
      }

      .btn-prev .el-icon,
      .btn-next .el-icon {
        font-size: 12px;
      }
    }
  }
}
</style>
